import { MouseEvent, useCallback, useState } from 'react'
import { ClickAwayListener, Popper, styled } from '@mui/material'
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import CircularProgress from '@mui/material/CircularProgress'
import { Button } from '../../components/buttons'
import { Menu, MenuItem, MenuItemText, PopperRoot } from '../../components/menu'
import { intl } from '../../../i18n'
import {
  OPENAPI_DOCS_FORMATS,
  OpenApiDocsFormat,
  useDownloadOpenApiDocs,
} from '../../hooks/useDownloadOpenApiDocs'

type Props = {
  functionUuid: string
}

// OpenAPI ドキュメントを HTML / JSON から選んでダウンロードするボタン。
// API トークン管理系の複数画面で共用する。表示可否は各画面のルート登録側のフラグで制御される。
export const OpenApiDocsDownloadButton = ({ functionUuid }: Props) => {
  const { handleDownload, isDownloading } = useDownloadOpenApiDocs(functionUuid)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpenMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSelectFormat = useCallback(
    (format: OpenApiDocsFormat) => {
      setAnchorEl(null)
      handleDownload(format)
    },
    [handleDownload]
  )

  const isMenuOpen = anchorEl !== null

  return (
    <>
      <Button
        variant="outlined"
        colorPattern="monotone"
        size="m"
        onClick={handleOpenMenu}
        disabled={isDownloading}
        sx={{ marginLeft: '16px' }}
      >
        {isDownloading ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <GetAppRoundedIcon fontSize="small" />
        )}
        {intl.formatMessage({ id: 'openApiDocs.download' })}
        {/* メニューが開くことを示すキャレット。開いている間は回転させて状態を可視化する */}
        <ExpandMoreCaret fontSize="small" open={isMenuOpen} />
      </Button>
      <Popper
        open={isMenuOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleCloseMenu}>
          <PopperRoot>
            <Menu>
              {OPENAPI_DOCS_FORMATS.map(format => (
                <MenuItem
                  key={format}
                  onClick={() => handleSelectFormat(format)}
                >
                  <MenuItemText>
                    {intl.formatMessage({ id: `openApiDocs.format.${format}` })}
                  </MenuItemText>
                </MenuItem>
              ))}
            </Menu>
          </PopperRoot>
        </ClickAwayListener>
      </Popper>
    </>
  )
}

const ExpandMoreCaret = styled(ExpandMoreRoundedIcon, {
  shouldForwardProp: prop => prop !== 'open',
})<{ open: boolean }>(({ open }) => ({
  transform: open ? 'rotate(180deg)' : 'none',
  transition: 'transform .2s',
}))
